import EditIcon from '@mui/icons-material/Edit'
import { Dialog, IconButton, Tooltip } from '@mui/material'
import cn from 'classnames'
import React, { useEffect, useState } from 'react'
import get from '../helpers/Api'
import { useTranslation } from 'react-i18next'
import { Popover, PopoverContent, PopoverTrigger } from '@nextui-org/react'
import { DropButton } from '../drops/DropButton'
import { DropAlert } from '../drops/DropAlert'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import FacebookIcon from '@mui/icons-material/Facebook'
import GitHubIcon from '@mui/icons-material/GitHub'
import LanguageIcon from '@mui/icons-material/Language'
import LinkIcon from '@mui/icons-material/Link'
import { useSharedState } from '../waxplorer/Waxplorer'
import TelegramIcon from '@mui/icons-material/Telegram'
import { DropPaper } from '../drops/DropPaper'
import TwitterIcon from '@mui/icons-material/Twitter'
import YouTubeIcon from '@mui/icons-material/YouTube'
import { DropLink } from '../drops/DropLink'

import MainButton from '../common/util/input/MainButton'

const CollectionPageTitle = ({
    collectionName,
    collectionImage,
    collectionThumbnail,
    verified,
    blacklisted,
    toggleImage,
    setUpdateLogoDialogOpen,
    collection,
    collectionMore = true,
}) => {
    const authorized = collection?.authorized
    const { t } = useTranslation('common')
    const [state] = useSharedState()
    const [isBlacklisted, setIsBlacklisted] = useState(false)

    const activeUser = state?.activeUser
    const userName = activeUser?.accountName
        ? state?.activeUser.accountName
        : null

    const addBlackList = async () => {
        try {
            await activeUser.session.transact(
                {
                    actions: [
                        {
                            account: 'atomhubtools',
                            name: 'addaccvalues',
                            authorization: [
                                {
                                    actor: userName,
                                    permission: activeUser.requestPermission
                                        ? activeUser.requestPermission
                                        : 'active',
                                },
                            ],
                            data: {
                                account: userName,
                                list_name: 'col.blist',
                                values_to_add: [collectionName],
                            },
                        },
                    ],
                },
                {
                    expireSeconds: 300,
                    blocksBehind: 0,
                },
            )
            setIsBlacklisted(true)
        } catch (e) {
            setError(e.message)
        }
    }

    const remBlackList = async () => {
        try {
            await activeUser.session.transact(
                {
                    actions: [
                        {
                            account: 'atomhubtools',
                            name: 'remaccvalues',
                            authorization: [
                                {
                                    actor: userName,
                                    permission: activeUser.requestPermission
                                        ? activeUser.requestPermission
                                        : 'active',
                                },
                            ],
                            data: {
                                account: userName,
                                list_name: 'col.blist',
                                values_to_add: [collectionName],
                            },
                        },
                    ],
                },
                {
                    expireSeconds: 300,
                    blocksBehind: 0,
                },
            )
            setIsBlacklisted(false)
        } catch (e) {
            setError(e.message)
        }
    }

    const parseIsBlacklisted = (res) => {
        setIsBlacklisted(
            res && res.length > 0 && res[0]['collection'] === collectionName,
        )
    }

    useEffect(() => {
        if (userName) {
            get(
                `get-personal-blacklist?limit=1&user=${userName}&term=${collectionName}`,
                'legacy',
            ).then(parseIsBlacklisted)
        }
    }, [userName])

    const [error, setError] = useState(null)

    const image =
        collectionThumbnail && process.env.NEXT_PUBLIC_TESTNET !== 'TRUE'
            ? collectionThumbnail
            : collectionImage

    const [authAcctsDialogOpen, setAuthAcctsDialogOpen] = useState(false)

    return (
        <div className="flex flex-col items-start justify-end flex-1 gap-2">
            <div>
                {image && !blacklisted ? (
                    <div
                        className={cn(
                            'w-36 h-36 cursor-pointer z-20',
                            'overflow-hidden border-none rounded-md relative backdrop-blur',
                            verified ? 'border-primary' : 'border-neutral',
                        )}
                    >
                        {setUpdateLogoDialogOpen ? (
                            <div className="absolute bg-black rounded-full hover:bg-graphbg top-1 right-1">
                                <IconButton
                                    color="primary"
                                    size="small"
                                    onClick={() =>
                                        setUpdateLogoDialogOpen(true)
                                    }
                                >
                                    <Tooltip
                                        title={t('collection.update_logo')}
                                    >
                                        <EditIcon fontSize="small" />
                                    </Tooltip>
                                </IconButton>
                            </div>
                        ) : null}
                        <img
                            className="object-contain h-full m-auto "
                            src={image}
                            alt=""
                            onClick={() => toggleImage(collectionImage)}
                        />
                    </div>
                ) : (
                    ''
                )}
            </div>

            <div className="flex flex-col ">
                <div className="flex items-center">
                    <div
                        className={cn(
                            ' md:mr-2 lg:mr-0 lg2:mr-2 font-bold md:font-semibold text-4xl md:bg-transparent ',
                            verified ? 'text-primary' : '',
                        )}
                    >
                        {collectionName}
                    </div>
                    {verified && (
                        <img
                            src={'/verified.svg'}
                            width={24}
                            height={24}
                            alt="verified"
                            className="w-[24px] h-[24px] ml-2"
                        />
                    )}
                </div>
                <Dialog
                    open={authAcctsDialogOpen}
                    onClose={() => setAuthAcctsDialogOpen(false)}
                    fullWidth
                >
                    <DropPaper noMargin title="Authorized Accounts">
                        <div className="flex flex-col justify-center p-2 ">
                            <div className="flex flex-wrap justify-center w-full ">
                                {authorized?.map((acct) => (
                                    <div
                                        key={'acct_' + acct}
                                        className="flex gap-2 "
                                    >
                                        <DropLink
                                            href={`/user/${acct}`}
                                            external
                                            className="p-1 m-2 border rounded-md cursor-pointer border-primary text-primary hover:bg-primary hover:text-white"
                                        >
                                            {acct}
                                        </DropLink>
                                    </div>
                                ))}
                            </div>
                            <div className="flex justify-center mt-2 ">
                                <MainButton
                                    className="bg-red-500 w-[82px] "
                                    onClick={() => {
                                        setAuthAcctsDialogOpen(false)
                                    }}
                                >
                                    Close
                                </MainButton>
                            </div>
                        </div>
                    </DropPaper>
                </Dialog>

                <div className="flex flex-wrap items-center justify-start mt-2">
                    {collection?.authorized ? (
                        <Tooltip title="Authorized Accounts">
                            <IconButton
                                size="small"
                                onClick={() => setAuthAcctsDialogOpen(true)}
                                className="text-white "
                            >
                                <AdminPanelSettingsIcon />
                            </IconButton>
                        </Tooltip>
                    ) : null}
                    {collection?.url ? (
                        <Tooltip title="Website">
                            <a
                                href={
                                    collection?.url.startsWith('http')
                                        ? collection?.url
                                        : '//' + collection?.url
                                }
                                target="_blank"
                            >
                                <IconButton
                                    size="small"
                                    className="text-white "
                                >
                                    <LanguageIcon />
                                </IconButton>
                            </a>
                        </Tooltip>
                    ) : null}
                    {collection?.socials?.telegram ||
                    collection?.socials?.telegram ? (
                        <Tooltip title="Telegram">
                            <a
                                href={
                                    collection.socials.telegram
                                        ? collection.socials.telegram.startsWith(
                                              'https://telegram.me/',
                                          ) ||
                                          collection.socials.telegram.startsWith(
                                              'https://t.me/',
                                          )
                                            ? collection.socials.telegram
                                            : 'https://telegram.me/' +
                                              collection.socials.telegram
                                        : telegram.startsWith(
                                              'https://telegram.me/',
                                          )
                                        ? telegram
                                        : 'https://telegram.me/' + telegram
                                }
                                target="_blank"
                            >
                                <IconButton size="small">
                                    <TelegramIcon />
                                </IconButton>
                            </a>
                        </Tooltip>
                    ) : null}{' '}
                    {collection?.socials?.twitter ? (
                        <Tooltip title="twitter">
                            <a
                                href={
                                    collection.socials.twitter.startsWith(
                                        'https://twitter.com/',
                                    )
                                        ? collection.socials.twitter
                                        : 'https://twitter.com/' +
                                          collection.socials.twitter
                                }
                                target="_blank"
                            >
                                <IconButton size="small">
                                    <TwitterIcon />
                                </IconButton>
                            </a>
                        </Tooltip>
                    ) : null}
                    {collection?.socials?.youtube ? (
                        <Tooltip title="YouTube">
                            <a
                                href={
                                    collection.socials.youtube.startsWith(
                                        'https://www.youtube.com/@',
                                    ) ||
                                    collection.socials.youtube.startsWith(
                                        'https://www.youtube.com/c',
                                    )
                                        ? collection.socials.youtube
                                        : collection.socials.youtube.startsWith(
                                              '@',
                                          )
                                        ? 'https://www.youtube.com/' +
                                          collection.socials.youtube
                                        : 'https://www.youtube.com/@' +
                                          collection.socials.youtube
                                }
                                target="_blank"
                            >
                                <IconButton size="small">
                                    <YouTubeIcon />
                                </IconButton>
                            </a>
                        </Tooltip>
                    ) : null}
                    {collection?.socials?.facebook ? (
                        <Tooltip title="Facebook">
                            <a
                                href={
                                    collection.socials.facebook.startsWith(
                                        'https://www.facebook.com/',
                                    )
                                        ? collection.socials.facebook
                                        : 'https://www.facebook.com/' +
                                          collection.socials.facebook
                                }
                                target="_blank"
                            >
                                <IconButton size="small">
                                    <FacebookIcon />
                                </IconButton>
                            </a>
                        </Tooltip>
                    ) : null}
                    {collection?.socials?.github ? (
                        <Tooltip title="GitHub">
                            <a
                                href={
                                    collection.socials.github.startsWith(
                                        'https://github.com/',
                                    )
                                        ? collection.socials.github
                                        : 'https://github.com/' +
                                          collection.socials.github
                                }
                                target="_blank"
                            >
                                <IconButton size="small">
                                    <GitHubIcon />
                                </IconButton>
                            </a>
                        </Tooltip>
                    ) : null}
                    {collection?.socials?.discord ? (
                        <Tooltip title="Discord">
                            <a
                                href={
                                    collection.socials.discord.startsWith(
                                        'https://discord.gg/',
                                    )
                                        ? collection.socials.discord
                                        : 'https://discord.gg/' +
                                          collection.socials.discord
                                }
                                target="_blank"
                            >
                                <IconButton size="small">
                                    <LinkIcon />
                                </IconButton>
                            </a>
                        </Tooltip>
                    ) : null}
                    {collection?.socials?.medium ? (
                        <Tooltip title="Medium">
                            <a
                                href={
                                    collection.socials.medium.startsWith(
                                        'https://medium.com/',
                                    )
                                        ? collection.socials.medium
                                        : 'https://medium.com/' +
                                          collection.socials.medium
                                }
                                target="_blank"
                            >
                                <IconButton size="small">
                                    <LinkIcon />
                                </IconButton>
                            </a>
                        </Tooltip>
                    ) : null}
                    {collectionMore ? (
                        <Popover placement="right items-center">
                            <PopoverTrigger>
                                <img
                                    className="items-center w-6 h-6 rotate-90 cursor-pointer"
                                    src={'/more.svg'}
                                    alt="more"
                                />
                            </PopoverTrigger>

                            <PopoverContent>
                                {userName && (
                                    <div className="mt-0 ">
                                        {isBlacklisted && userName ? (
                                            <DropButton
                                                className="w-full md:w-64"
                                                fullWidth
                                                color="error"
                                                onClick={remBlackList}
                                            >
                                                {t(
                                                    'collection.remove_blacklist',
                                                )}
                                            </DropButton>
                                        ) : (
                                            !isBlacklisted &&
                                            userName && (
                                                <DropButton
                                                    className="w-full md:w-64"
                                                    fullWidth
                                                    onClick={addBlackList}
                                                    color="default"
                                                    variant="solid"
                                                >
                                                    {t(
                                                        'collection.add_personal_blacklist',
                                                    )}
                                                </DropButton>
                                            )
                                        )}
                                        <DropAlert
                                            condition={error !== null}
                                            severity="error"
                                        >
                                            {error}
                                        </DropAlert>
                                    </div>
                                )}
                            </PopoverContent>
                        </Popover>
                    ) : null}
                </div>
            </div>
        </div>
    )
}

export default CollectionPageTitle
